import { useEffect, useMemo, useState } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { MapPin, Truck, Store, Loader2 } from "lucide-react";

// Leaflet's default marker icon paths break under bundlers — rebind them.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Small coloured pin generator so the three markers are distinguishable.
const dot = (color) => L.divIcon({
  className: "delivery-pin",
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  html: `<div style="width:26px;height:26px;border-radius:50%;background:${color};border:3px solid #ffffff;box-shadow:0 2px 8px rgba(0,0,0,0.25);"></div>`,
});
const ICONS = {
  shop: dot("var(--shop-primary,#0055FF)"),
  employee: dot("#FF5000"),
  customer: dot("#059669"),
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    const valid = points.filter((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]));
    if (valid.length === 0) return;
    if (valid.length === 1) {
      map.setView(valid[0], 15);
      return;
    }
    map.fitBounds(valid, { padding: [40, 40], maxZoom: 15 });
  }, [map, points]);
  return null;
}

/**
 * DeliveryMap — customer-side live tracker for a single order.
 *
 * Renders a Leaflet map with up to three pins (shop / employee / customer)
 * plus a polyline shop → employee → customer. Polls the tracking endpoint
 * every 15 seconds while the order is out for delivery.
 */
export default function DeliveryMap({ orderId }) {
  const [data, setData] = useState(null);
  const [customerCoord, setCustomerCoord] = useState(null); // browser geolocation
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/store/orders/${orderId}/track`);
        if (!cancelled) setData(data);
      } catch {
        /* silent — the parent decides whether to show the map */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = window.setInterval(load, 15000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [orderId]);

  // Ask the browser (once) for the customer's own coordinates so we can pin
  // "your door" — falls back gracefully if the customer declines.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCustomerCoord([pos.coords.latitude, pos.coords.longitude]),
      () => setCustomerCoord(null),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  }, []);

  const shop = data?.shop || {};
  const emp = data?.employee || {};
  const cust = data?.customer || {};

  const shopPt = Number.isFinite(shop.lat) && Number.isFinite(shop.lng) ? [shop.lat, shop.lng] : null;
  const empPt = Number.isFinite(emp.lat) && Number.isFinite(emp.lng) ? [emp.lat, emp.lng] : null;
  const custPt = customerCoord;

  const line = useMemo(() => {
    return [shopPt, empPt, custPt].filter(Boolean);
  }, [shopPt, empPt, custPt]);

  if (loading) {
    return (
      <div className="border border-slate-200 rounded-md bg-slate-50 h-56 flex items-center justify-center text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!shopPt && !empPt) {
    return (
      <div
        className="border border-slate-200 rounded-md bg-slate-50 p-3 text-xs text-slate-600 flex items-start gap-2"
        data-testid={`order-track-empty-${orderId}`}
      >
        <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
        <div>
          {data?.status === "awaiting_otp"
            ? "Your delivery person has arrived. Read out the OTP above to complete delivery."
            : "The delivery person's live location will appear here once they leave the shop."}
        </div>
      </div>
    );
  }

  const center = empPt || shopPt || custPt || [22.5726, 88.3639];

  return (
    <div
      className="border border-slate-200 rounded-md overflow-hidden"
      data-testid={`order-track-map-${orderId}`}
    >
      <div className="h-64">
        <MapContainer
          center={center}
          zoom={13}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {shopPt && (
            <Marker position={shopPt} icon={ICONS.shop}>
              <Popup><b>Shop</b><br />{shop.address || "Ships from here"}</Popup>
            </Marker>
          )}
          {empPt && (
            <Marker position={empPt} icon={ICONS.employee}>
              <Popup>
                <b>{data?.assigned_employee_name || "Delivery"}</b>
                <br />
                Live · updated {emp.updated_at ? new Date(emp.updated_at).toLocaleTimeString() : "just now"}
              </Popup>
            </Marker>
          )}
          {custPt && (
            <Marker position={custPt} icon={ICONS.customer}>
              <Popup><b>Your door</b><br />{cust.address}</Popup>
            </Marker>
          )}
          {line.length >= 2 && (
            <Polyline positions={line} pathOptions={{ color: "#FF5000", weight: 4, dashArray: "8,6" }} />
          )}
          <FitBounds points={line} />
        </MapContainer>
      </div>
      <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 flex items-center gap-3 text-[11px] mono text-slate-600">
        <span className="flex items-center gap-1"><Store className="w-3 h-3 text-[var(--shop-primary,#0055FF)]" /> Shop</span>
        <span className="flex items-center gap-1"><Truck className="w-3 h-3 text-[#FF5000]" /> Delivery</span>
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-emerald-600" /> You</span>
        <span className="ml-auto text-slate-400">Refreshes every 15s</span>
      </div>
    </div>
  );
}
